import { downloadCsv, toCsvRow } from './downloadCsv';
import {
  getGymExportBrand,
  getGymExportSlug,
  getOwnerExportBrand,
  getOwnerExportSlug,
} from './gymExportBrand';
import { INVOICE_TERMS, INVOICE_TERMS_TITLE, buildInvoiceTermsHtml } from './invoiceTerms';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/** @deprecated Prefer brand: 'owner' or getOwnerExportBrand() — resolves live from Settings. */
export const SOFTWARE_BRAND = 'owner';

export function getExportFileDate() {
  return new Date().toISOString().slice(0, 10);
}

export function getExportTimestamp() {
  return new Date().toLocaleString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function resolveBrand(brand) {
  if (typeof brand === 'function') return brand();
  if (brand === 'owner' || brand === 'platform') return getOwnerExportBrand();
  if (brand && typeof brand === 'object') return brand;
  return getGymExportBrand();
}

function resolveFileSlug(slug, brand) {
  const brandKey = brand === 'owner' || brand === 'platform' || brand === SOFTWARE_BRAND
    ? getOwnerExportSlug()
    : (typeof brand === 'object' && brand?.name
      ? String(brand.name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      : getGymExportSlug());
  return slug ? `${slug}-${brandKey || 'export'}` : (brandKey || 'export');
}

function buildCsvContent({ title, headers, rows, summary = [], brand }) {
  const resolvedBrand = resolveBrand(brand);
  const metaLines = brandMetaLines(resolvedBrand);
  return [
    resolvedBrand.name,
    ...metaLines,
    title,
    `Generated: ${getExportTimestamp()}`,
    `Total Records: ${rows.length}`,
    '',
    toCsvRow(headers),
    ...rows.map((row) => toCsvRow(row)),
    ...(summary.length ? ['', 'Summary', ...summary] : []),
  ].join('\n');
}
const DOCUMENT_STYLES = `
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 28px; color: #1e293b; background: #fff; }
  .header { display: flex; justify-content: space-between; gap: 20px; border-bottom: 3px solid #6bb800; padding-bottom: 16px; margin-bottom: 22px; align-items: flex-start; }
  .brand-row { display: flex; align-items: flex-start; gap: 16px; }
  .brand-logo { width: 80px; height: 80px; object-fit: contain; border-radius: 8px; flex-shrink: 0; }
  .brand-block { display: flex; flex-direction: column; align-items: flex-start; justify-content: center; min-height: 80px; }
  .brand { font-size: 24px; font-weight: 700; color: #6bb800; margin: 0 0 6px; line-height: 1.25; }
  .meta { font-size: 13px; color: #64748b; line-height: 1.55; }
  .doc-badge { text-align: right; font-size: 12px; color: #64748b; }
  .doc-badge strong { display: block; font-size: 18px; color: #0f172a; margin-bottom: 4px; }
  .doc-title { font-size: 20px; font-weight: 700; margin: 0 0 6px; color: #0f172a; }
  .doc-sub { font-size: 13px; color: #64748b; margin: 0 0 18px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: #6bb800; color: #fff; text-align: left; padding: 10px 12px; font-weight: 600; }
  td { padding: 9px 12px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  tr:nth-child(even) td { background: #f8fafc; }
  .empty { text-align: center; color: #94a3b8; padding: 24px !important; }
  .invoice-grid { border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-top: 8px; }
  .invoice-row { display: flex; justify-content: space-between; gap: 16px; padding: 11px 16px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
  .invoice-row:nth-child(even) { background: #f8fafc; }
  .invoice-row:last-child { border-bottom: none; font-weight: 700; background: #f0fdf4; color: #166534; }
  .invoice-row span { color: #64748b; }
  .invoice-row strong { color: #0f172a; text-align: right; }
  .summary { margin-top: 20px; padding: 14px 16px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
  .summary h3 { margin: 0 0 8px; font-size: 14px; color: #0f172a; }
  .summary ul { margin: 0; padding-left: 18px; font-size: 13px; color: #475569; line-height: 1.7; }
  .invoice-terms { margin-top: 18px; padding-top: 12px; border-top: 1px dashed #94a3b8; }
  .invoice-terms-title {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 800;
    text-decoration: underline;
    color: #0f172a;
    text-transform: none;
  }
  .invoice-terms-list {
    margin: 0;
    padding-left: 18px;
    font-size: 12px;
    color: #334155;
    line-height: 1.55;
  }
  .invoice-terms-list li { margin-bottom: 4px; }
  .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; text-align: center; }
  @media print {
    body { padding: 12px; }
    @page { margin: 12mm; }
  }
`;

/** Narrow thermal slip (bank token / mall POS ~80mm).
 * Left margin is intentionally large — many 80mm printers clip ~3–5mm on the feed side.
 * Each `.thermal-slip` is one page so POS drivers can auto-cut between copies.
 */
const THERMAL_INVOICE_STYLES = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
  }
  body {
    width: 64mm;
    max-width: 64mm;
    margin: 0 0 0 6mm;
    padding: 0;
    font-size: 13px;
    line-height: 1.4;
  }
  .thermal-slip {
    width: 64mm;
    max-width: 64mm;
    padding: 3mm 2mm 6mm 0;
    page-break-after: always;
    break-after: page;
  }
  .thermal-slip + .thermal-slip {
    page-break-before: always;
    break-before: page;
  }
  .thermal-slip:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .thermal-copy-label {
    margin: 0 0 6px;
    padding: 3px 0;
    border: 1px dashed #000;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-align: center;
    text-transform: uppercase;
  }
  .header { display: none; }
  .brand-row, .brand-logo, .brand-block, .brand, .meta, .doc-badge, .doc-sub { display: none; }
  .doc-title {
    font-size: 16px;
    font-weight: 800;
    margin: 0 0 8px;
    padding-bottom: 6px;
    border-bottom: 1px dashed #000;
    color: #000;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .invoice-grid {
    border: none;
    border-radius: 0;
    margin-top: 0;
    overflow: visible;
  }
  .invoice-row {
    display: flex;
    justify-content: space-between;
    gap: 6px;
    padding: 4px 0;
    border-bottom: 1px dotted #bbb;
    font-size: 12px;
  }
  .invoice-row:nth-child(even) { background: transparent; }
  .invoice-row:last-child {
    border-bottom: 1px dashed #000;
    border-top: 1px dashed #000;
    margin-top: 4px;
    padding-top: 6px;
    padding-bottom: 6px;
    font-weight: 800;
    background: transparent;
    color: #000;
    font-size: 13px;
  }
  .invoice-row span {
    color: #333;
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 1px;
  }
  .invoice-row strong {
    color: #000;
    text-align: right;
    max-width: 52%;
    word-break: break-word;
    flex: 0 1 auto;
  }
  .summary { display: none; }
  .invoice-terms {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px dashed #000;
  }
  .invoice-terms-title {
    margin: 0 0 4px;
    font-size: 11px;
    font-weight: 800;
    text-decoration: underline;
    color: #000;
    text-align: left;
  }
  .invoice-terms-list {
    margin: 0;
    padding-left: 14px;
    font-size: 10px;
    color: #111;
    line-height: 1.45;
  }
  .invoice-terms-list li { margin-bottom: 3px; }
  .footer {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px dashed #000;
    font-size: 10px;
    color: #333;
    text-align: center;
  }
  .thermal-cut-feed {
    height: 14mm;
  }
  @page {
    size: 80mm auto;
    margin: 2mm 2mm 2mm 6mm;
  }
  @media print {
    html, body {
      width: 64mm !important;
      max-width: 64mm !important;
    }
    body {
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .thermal-slip {
      page-break-after: always !important;
      break-after: page !important;
    }
    .thermal-slip + .thermal-slip {
      page-break-before: always !important;
      break-before: page !important;
    }
    .thermal-slip:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
  }
`;

function brandMetaLines(resolvedBrand) {
  const lines = [];
  const branch = String(resolvedBrand.branch || '').trim();
  if (branch && branch !== 'Gym Administration' && branch !== 'Owner Panel') {
    lines.push(branch);
  }
  const address = String(resolvedBrand.address || '').trim();
  if (address && address !== '—') lines.push(address);
  const phone = String(resolvedBrand.phone || '').trim();
  if (phone && phone !== '—') lines.push(phone);
  return lines;
}

function brandHeaderHtml(resolvedBrand) {
  const logo = resolvedBrand.logo
    ? `<img class="brand-logo" src="${escapeHtml(resolvedBrand.logo)}" alt="" />`
    : '';
  const metaLines = brandMetaLines(resolvedBrand);
  const metaHtml = metaLines.length
    ? `<div class="meta">${metaLines.map((line) => escapeHtml(line)).join('<br />')}</div>`
    : '';
  return `
    <div class="brand-row">
      ${logo}
      <div class="brand-block">
        <p class="brand">${escapeHtml(resolvedBrand.name)}</p>
        ${metaHtml}
      </div>
    </div>`;
}

function buildReportHtml({ title, headers, rows, summary = [], documentType = 'report', brand }) {
  const resolvedBrand = resolveBrand(brand);
  const headerCells = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const bodyRows = rows.length
    ? rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${headers.length}" class="empty">No records found</td></tr>`;

  const summaryHtml = summary.length
    ? `<div class="summary"><h3>Summary</h3><ul>${summary.map((s) => `<li>${escapeHtml(s)}</li>`).join('')}</ul></div>`
    : '';

  const badgeLabel = documentType === 'invoice' ? 'INVOICE' : 'REPORT';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} - ${escapeHtml(resolvedBrand.name)}</title>
  <style>${DOCUMENT_STYLES}</style>
</head>
<body>
  <div class="header">
    ${brandHeaderHtml(resolvedBrand)}
    <div class="doc-badge">
      <strong>${badgeLabel}</strong>
      ${escapeHtml(getExportTimestamp())}
    </div>
  </div>
  <h2 class="doc-title">${escapeHtml(title)}</h2>
  <p class="doc-sub">Total Records: ${rows.length}</p>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
  ${summaryHtml}
  <div class="footer">${escapeHtml(resolvedBrand.name)} — ${documentType === 'invoice' ? 'Official Invoice / Receipt' : 'Confidential Business Report'}</div>
</body>
</html>`;
}

function buildInvoiceHtml({
  title,
  fields,
  summary = [],
  brand,
  terms = true,
  thermal = false,
  copies = 1,
} = {}) {
  const resolvedBrand = resolveBrand(brand);
  const rowsHtml = fields
    .map(
      (field) => `
      <div class="invoice-row">
        <span>${escapeHtml(field.label)}</span>
        <strong>${escapeHtml(field.value)}</strong>
      </div>`,
    )
    .join('');

  const summaryHtml = summary.length
    ? `<div class="summary"><h3>Notes</h3><ul>${summary.map((s) => `<li>${escapeHtml(s)}</li>`).join('')}</ul></div>`
    : '';

  const termsHtml = terms ? buildInvoiceTermsHtml(escapeHtml) : '';
  const styles = thermal ? THERMAL_INVOICE_STYLES : DOCUMENT_STYLES;
  const dateRow = thermal
    ? `<div class="invoice-row"><span>Date</span><strong>${escapeHtml(getExportTimestamp())}</strong></div>`
    : '';
  const headerHtml = thermal
    ? `<h2 class="doc-title">Bill</h2>`
    : `
  <div class="header">
    ${brandHeaderHtml(resolvedBrand)}
    <div class="doc-badge">
      <strong>INVOICE</strong>
      ${escapeHtml(getExportTimestamp())}
    </div>
  </div>
  <h2 class="doc-title">${escapeHtml(title)}</h2>
  <p class="doc-sub">Official payment receipt</p>`;
  const footerHtml = thermal
    ? 'Thank you for your business'
    : `${escapeHtml(resolvedBrand.name)} — Thank you for your business`;

  const slipBody = `
  ${headerHtml}
  <div class="invoice-grid">${dateRow}${rowsHtml}</div>
  ${summaryHtml}
  ${termsHtml}
  <div class="footer">${footerHtml}</div>`;

  // Thermal: always 2 slips (office + customer) unless caller asks otherwise.
  const copyCount = thermal
    ? Math.max(2, Math.min(4, Number(copies) || 2))
    : 1;
  const copyLabels = ['Merchant Copy', 'Customer Copy', 'Copy 3', 'Copy 4'];
  const bodyHtml = thermal
    ? Array.from({ length: copyCount }, (_, index) => `
  <section class="thermal-slip">
    <div class="thermal-copy-label">${escapeHtml(copyLabels[index] || `Copy ${index + 1}`)}</div>
    ${slipBody}
    <div class="thermal-cut-feed" aria-hidden="true"></div>
  </section>`).join('')
    : slipBody;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(thermal ? 'Bill' : title)} - ${escapeHtml(resolvedBrand.name)}</title>
  <style>${styles}</style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 200);
}

/** Build and download a real .pdf file (no print dialog / popup). */
function downloadReportPdf({ title, headers, rows, summary = [], brand }, filename) {
  const resolvedBrand = resolveBrand(brand);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const marginX = 36;
  let y = 40;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(107, 184, 0);
  doc.text(String(resolvedBrand.name || 'Export'), marginX, y);
  y += 18;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(String(title || 'Report'), marginX, y);
  y += 14;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(`Generated: ${getExportTimestamp()}`, marginX, y);
  y += 12;
  doc.text(`Total records: ${rows.length}`, marginX, y);
  y += 8;

  const meta = brandMetaLines(resolvedBrand);
  meta.forEach((line) => {
    y += 11;
    doc.text(String(line), marginX, y);
  });
  y += 10;

  autoTable(doc, {
    startY: y,
    head: [headers.map((h) => String(h ?? ''))],
    body: (rows || []).map((row) => (row || []).map((cell) => String(cell ?? '—'))),
    styles: {
      font: 'helvetica',
      fontSize: 7,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: {
      fillColor: [107, 184, 0],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: marginX, right: marginX },
  });

  if (summary?.length) {
    const afterTableY = (doc.lastAutoTable?.finalY || y) + 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text('Summary', marginX, afterTableY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    summary.forEach((line, index) => {
      doc.text(`• ${String(line)}`, marginX, afterTableY + 14 + (index * 12));
    });
  }

  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

/** Download a simple key/value invoice as a real .pdf file. */
function downloadInvoicePdf({ title, fields, summary = [], brand }, filename) {
  const resolvedBrand = resolveBrand(brand);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const marginX = 40;
  let y = 48;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(107, 184, 0);
  doc.text(String(resolvedBrand.name || 'Invoice'), marginX, y);
  y += 22;

  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(String(title || 'Invoice & Receipt'), marginX, y);
  y += 16;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(`Generated: ${getExportTimestamp()}`, marginX, y);
  y += 18;

  const rows = (fields || []).map((f) => [String(f.label || ''), String(f.value ?? '—')]);
  autoTable(doc, {
    startY: y,
    body: rows,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 6 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 160, textColor: [100, 116, 139] },
      1: { textColor: [15, 23, 42] },
    },
    margin: { left: marginX, right: marginX },
  });

  if (summary?.length) {
    const afterY = (doc.lastAutoTable?.finalY || y) + 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text('Summary', marginX, afterY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    summary.forEach((line, index) => {
      doc.text(`• ${String(line)}`, marginX, afterY + 14 + (index * 12));
    });
  }

  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

/**
 * Print without window.open — hidden iframe (popup blockers never apply).
 */
function printHtmlInPage(html) {
  if (typeof document === 'undefined') return false;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Export print');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);

  const cleanup = () => {
    window.setTimeout(() => {
      try { iframe.remove(); } catch { /* ignore */ }
    }, 60_000);
  };

  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      iframe.remove();
      return false;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const doPrint = () => {
      try {
        win.focus();
        win.print();
      } catch { /* ignore */ }
      cleanup();
    };

    // Allow layout/images a moment, then open the print dialog
    if (iframe.contentDocument?.readyState === 'complete') {
      window.setTimeout(doPrint, 300);
    } else {
      iframe.onload = () => window.setTimeout(doPrint, 300);
      window.setTimeout(doPrint, 1200);
    }
    return true;
  } catch {
    try { iframe.remove(); } catch { /* ignore */ }
    return false;
  }
}

/** @deprecated Prefer printHtmlInPage — kept so older call sites keep working. */
function openHtmlWindow(html, { autoPrint = false, existingWindow, targetWindow } = {}) {
  const preopened = existingWindow || targetWindow;
  if (preopened && !preopened.closed) {
    try {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      preopened.location.replace(url);
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
      if (autoPrint) {
        window.setTimeout(() => { try { preopened.print(); } catch { /* ignore */ } }, 800);
      }
      return true;
    } catch {
      /* fall through to iframe */
    }
  }
  return printHtmlInPage(html);
}

/** @deprecated Popup-based preview — prefer iframe print (no blocker). */
export function openExportPreviewWindow(message = 'Preparing export…') {
  const win = window.open('about:blank', '_blank');
  if (!win) return null;
  try {
    const doc = win.document;
    if (doc?.body) {
      doc.title = 'Preparing…';
      doc.body.style.cssText = 'font-family:Segoe UI,Tahoma,sans-serif;padding:28px;color:#64748b;';
      doc.body.textContent = message;
    }
  } catch {
    /* ignore */
  }
  return win;
}

export function createProfessionalExporter({
  title,
  headers,
  slug,
  getRows,
  getSummary,
  documentType = 'report',
  brand,
}) {
  const resolve = (data) => ({
    title,
    headers,
    rows: getRows(data),
    summary: getSummary?.(data) || [],
    documentType,
    brand: resolveBrand(brand),
  });

  const fileSlug = () => resolveFileSlug(slug, brand);

  return {
    print(data) {
      const config = resolve(data);
      printHtmlInPage(buildReportHtml(config));
    },
    csv(data) {
      const config = resolve(data);
      downloadCsv(buildCsvContent(config), `${fileSlug()}-${getExportFileDate()}.csv`);
    },
    excel(data) {
      const config = resolve(data);
      const html = buildReportHtml(config);
      downloadBlob(`\ufeff${html}`, `${fileSlug()}-${getExportFileDate()}.xls`, 'application/vnd.ms-excel;charset=utf-8;');
    },
    pdf(data) {
      const config = resolve(data);
      downloadReportPdf(config, `${fileSlug()}-${getExportFileDate()}.pdf`);
    },
    export(data) {
      this.csv(data);
    },
  };
}

export function printProfessionalInvoice({
  title = 'Invoice & Receipt',
  fields,
  summary = [],
  brand,
  terms = true,
  thermal = true,
  /** Thermal only: print N slips in one job (page-break → auto-cut on most POS printers). */
  copies = 2,
} = {}) {
  printHtmlInPage(buildInvoiceHtml({
    title,
    fields,
    summary,
    brand,
    terms,
    thermal,
    copies: thermal ? copies : 1,
  }));
}

export function exportProfessionalInvoicePdf({
  title = 'Invoice & Receipt',
  fields,
  summary = [],
  brand,
  terms = true,
  thermal = false,
} = {}) {
  downloadInvoicePdf(
    { title, fields, summary, brand },
    `invoice-${getGymExportSlug()}-${getExportFileDate()}.pdf`,
  );
}

export function exportProfessionalInvoiceExcel({
  title = 'Invoice & Receipt',
  fields,
  summary = [],
  slug = 'invoice',
  brand,
  terms = true,
} = {}) {
  const html = buildInvoiceHtml({ title, fields, summary, brand, terms, thermal: false });
  const gymSlug = getGymExportSlug();
  downloadBlob(`\ufeff${html}`, `${slug}-${gymSlug}-${getExportFileDate()}.xls`, 'application/vnd.ms-excel;charset=utf-8;');
}

export function exportProfessionalInvoiceCsv({
  title = 'Invoice & Receipt',
  fields,
  slug = 'invoice',
  brand,
  terms = true,
} = {}) {
  const resolvedBrand = resolveBrand(brand);
  const gymSlug = getGymExportSlug();
  const metaLines = brandMetaLines(resolvedBrand);
  const lines = [
    resolvedBrand.name,
    ...metaLines,
    title,
    `Generated: ${getExportTimestamp()}`,
    '',
    ...fields.map((field) => toCsvRow([field.label, field.value])),
  ];
  if (terms) {
    lines.push('', INVOICE_TERMS_TITLE, ...INVOICE_TERMS.map((line, i) => `${i + 1}. ${line}`));
  }
  downloadCsv(lines.join('\n'), `${slug}-${gymSlug}-${getExportFileDate()}.csv`);
}
